import React, { useEffect, useRef, useState } from 'react';
import { inputs } from '@ecosystem/newron-design-system';
import { Instructions } from '../../content/instructions';
import { getIdentifiers } from '../../data/helpers';
import { Loading } from '../../design/loading/Loading';
import { DataContentProps } from '../../newron-core';
import { ColumnType, DataCellValue, DataColumn, ExportFormat, ExportOptions, IdentifierColumn } from '../../types';
import { ExportButton } from '../../export/export.button';
import { statsToExportData } from './statsToExportData';
import { ExcelExportDto, PowerpointExportDto } from '@ecosystem/export-api';
import { chartToPptData } from '../../export';
import { useCorePageState } from '../../page/provider';
import { ExportHideout } from '../../export/ExportHideout';
import { splitIntoChunks } from '../../tools/arrays';
import { PageSettingsManager } from '../../page/settings';
import { Formatting } from '../../data/formatting';

import './statsTable.css';
import { useMobile } from '../../context/mobile/mobile.provider';
import classNames from 'classnames';
import { StatRow } from './types';

function StatsTable({
  data,
  isLoading,
  instructions,
  componentSettings,
  ComponentActionsContainer,
  componentMetadata,
  trackEvent,
  componentHeadingTitle,
}: DataContentProps) {
  const [dataStamp, setDataStamp] = useState<number>();
  const [namesRow, setNamesRow] = useState<StatRow>();
  const [statsRows, setStatsRows] = useState<StatRow[]>([]);
  const [itemCount, setItemCount] = useState<number>(1);
  const [currency, setCurrency] = useState<string>();

  const [exportModalOpen, setExportModalOpen] = useState(false);
  const chartSliceRefs = useRef<HTMLDivElement[]>([]);
  const { dsl, updatePageSettings } = useCorePageState();

  const pageSettings = PageSettingsManager.getInstance().getPageSettings();
  const hideCommon = pageSettings?.hideCommon?.[0] === 'true';
  const myInstructions = new Instructions(instructions, componentSettings);

  const rowIdField = myInstructions.getString('rowIdField');
  const title = myInstructions.hasInstruction('title') ? myInstructions.getString('title') : undefined;
  const exportOptions = myInstructions.getObject('export') as ExportOptions;

  const { isMobile } = useMobile();

  const format = (value: DataCellValue, column: DataColumn): string => {
    if (value === null) {
      return '-';
    }

    if (column.type === ColumnType.Date) {
      return Formatting.format(String(value), { formatHint: column.formatHint, dataType: column.dataType });
    }

    return String(value);
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!data || data.stamp === dataStamp) {
      return;
    }

    const dataSrc = data.getGroupedData([rowIdField])[0];
    if (!dataSrc) {
      return;
    }

    const nameColumn = data.getDataColumns().find(col => col.name === rowIdField);
    if (!nameColumn) {
      return;
    }
    const namesRow: StatRow = {
      label: 'name',
      values: dataSrc.data.map(row => {
        const { name } = getIdentifiers(nameColumn as IdentifierColumn, row);
        return name;
      }),
    };

    const statsRows: StatRow[] = [];
    data
      .getDataColumns()
      .filter(col => col.name !== rowIdField && col.arrayLength === null)
      .forEach(col => {
        const statRow: StatRow = {
          label: col.label,
          values: [],
        };
        dataSrc.data.forEach(row => {
          const value = row.values[col.name];
          const formatted = format(value, col);
          statRow.values.push(formatted);
        });
        statsRows.push(statRow);
      });

    setCurrency(data.getCurrency());
    setNamesRow(namesRow);
    setStatsRows(statsRows);
    setDataStamp(data.stamp);
    setItemCount(dataSrc.data.length);
  }, [data, rowIdField]);

  const isMultiItems = itemCount > 1;
  const showHideCommon = isMultiItems && ComponentActionsContainer;

  const rows = !hideCommon
    ? statsRows
    : statsRows.filter(row => !row.values.every((value, _, list) => value === list[0]));
  const dimmedRows = !hideCommon
    ? []
    : statsRows.filter(row => row.values.every((value, _, list) => value === list[0]));

  const getExportData = async (exportFormat: ExportFormat): Promise<PowerpointExportDto | ExcelExportDto> => {
    const allColumns = data.getAllColumns();
    switch (exportFormat) {
      case ExportFormat.Xlsx:
        return statsToExportData(exportOptions, namesRow, rows, dimmedRows, currency);

      case ExportFormat.Pptx:
        return await chartToPptData(
          exportOptions,
          dsl,
          null,
          chartSliceRefs.current,
          componentSettings,
          allColumns,
          componentHeadingTitle
        );

      default:
        break;
    }
  };

  const sliceStatsData = (
    namesRow: StatRow,
    rows: StatRow[],
    dimmedRows: StatRow[]
  ): { namesRow: StatRow; rows: StatRow[]; dimmedRows: StatRow[] }[] => {
    const slices: { namesRow: StatRow; rows: StatRow[]; dimmedRows: StatRow[] }[] = [];
    const slideSize = 5;
    splitIntoChunks(namesRow.values, slideSize).forEach((namesSlice, idx) => {
      const slideStart = idx * slideSize;
      const slideEnd = (idx + 1) * slideSize;
      slices.push({
        namesRow: { ...namesRow, values: namesSlice },
        rows: rows.map(row => ({ ...row, values: row.values.slice(slideStart, slideEnd) })),
        dimmedRows: dimmedRows.map(row => ({
          ...row,
          values: row.values.slice(slideStart, slideEnd),
        })),
      });
    });
    return slices;
  };

  const { Checkbox, CheckboxState } = inputs;

  const StatsHeader = ({ namesRow, isMultiItems }: { namesRow: StatRow; isMultiItems: boolean }): JSX.Element => (
    <thead className={classNames({ 'sticky-header': isMobile })}>
      <tr className="bg-medium">
        <th className="bg-medium sticky left-0 z-2">&nbsp;</th>
        {namesRow.values.map(name => (
          <th key={name} className={`py-2 ${isMultiItems ? 'pr-8' : ''}`} align="left">
            {name}
          </th>
        ))}
      </tr>
    </thead>
  );

  const StatsRow = ({
    row,
    isMultiItems,
    dimmed = false,
  }: {
    row: StatRow;
    isMultiItems: boolean;
    dimmed?: boolean;
  }): JSX.Element => (
    <tr key={`row_${row.label}`} className="border-b border-light">
      <td className={`py-1 bg-white font-bold pr-6 sticky left-0 z-2 ${dimmed ? 'text-inactive' : ''}`}>{row.label}</td>
      {row.values.map((value, idx) => (
        <td
          key={`row_${row.label}_${idx}_${value}`}
          className={`prewrap ${isMultiItems ? 'pr-8' : ''} ${dimmed ? 'text-inactive' : ''}`}
          align={isMultiItems ? 'left' : 'right'}
        >
          {value}
        </td>
      ))}
    </tr>
  );

  const renderStatsTable = (
    isMultiItems,
    namesRow,
    rows,
    dimmedRows,
    ref?: (HTMLTableElement) => void
  ): JSX.Element => (
    <table ref={ref} className="w-full text-md relative nowrap" style={{ minWidth: 'fit-content' }}>
      {isMultiItems && namesRow && <StatsHeader namesRow={namesRow} isMultiItems={isMultiItems} />}
      <tbody>
        {rows.map((row, idx) => (
          <StatsRow key={idx} row={row} isMultiItems={isMultiItems} />
        ))}
        {dimmedRows.length > 0 && (
          <tr>
            <td colSpan={dimmedRows[0].values.length + 1} className="h-2 bg-medium" />
          </tr>
        )}
        {dimmedRows.map((row, idx) => (
          <StatsRow key={idx} row={row} isMultiItems={isMultiItems} dimmed />
        ))}
      </tbody>
    </table>
  );

  return (
    <div
      className={classNames('relative w-full max-w-full overflow-x-auto pb-4', { 'table-container h-450': isMobile })}
    >
      <Loading isActive={isLoading} withIndicator />
      {title && <div className="font-bold pb-3">{title}</div>}
      {renderStatsTable(isMultiItems, namesRow, rows, dimmedRows)}
      {exportModalOpen && namesRow && (
        <>
          {sliceStatsData(namesRow, rows, dimmedRows).map((slice, idx) => (
            <ExportHideout key={idx}>
              {renderStatsTable(
                isMultiItems,
                slice.namesRow,
                slice.rows,
                slice.dimmedRows,
                element => (chartSliceRefs.current[idx] = element)
              )}
            </ExportHideout>
          ))}
        </>
      )}
      {showHideCommon && (
        <ComponentActionsContainer>
          <span className="ml-4 mr-auto">
            <Checkbox
              onChange={() => updatePageSettings({ hideCommon: [`${!hideCommon}`] })}
              state={hideCommon ? CheckboxState.CHECKED : CheckboxState.UNCHECKED}
              label="Show differences only"
            />
          </span>
          {data && exportOptions && (
            <ExportButton
              trackingSourceData={data}
              componentMetadata={componentMetadata}
              componentSettings={componentSettings}
              disabled={isLoading}
              exportFormats={[ExportFormat.Xlsx, ExportFormat.Pptx]}
              getExportData={getExportData}
              exportContentRef={chartSliceRefs}
              onModalVisibilityChange={isVisible => setExportModalOpen(isVisible)}
              trackEvent={trackEvent}
            />
          )}
        </ComponentActionsContainer>
      )}
    </div>
  );
}

export { StatsTable };
